import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

const RAW_BASE_URL =
  process.env['NX_PUBLIC_API_BASE_URL'] || 'http://localhost:8080';
const BASE_URL = RAW_BASE_URL.endsWith('/')
  ? RAW_BASE_URL.slice(0, -1)
  : RAW_BASE_URL;

// --- Interfaces ---

export interface InternalAccount {
  id: string;
  type: string;
  attributes: {
    name: string;
    title: string | null;
    is_internal_private: boolean;
    deactivated_at: string | null;
    created_at: string;
    updated_at: string;
  };
}

export interface InternalAccountUser {
  id: string;
  type: string;
  attributes: {
    email: string;
    display_name: string | null;
    role: string;
    created_at: string;
    updated_at: string;
  };
}

export interface AssumedRole {
  id: string;
  type: string;
  attributes: {
    internal_user_id: string;
    account_id: string;
    assumed_role: string;
    reason: string;
    started_at: string;
    expires_at: string;
    ended_at: string | null;
    created_at: string;
    updated_at: string;
    account_name: string;
    account_title: string;
    user_email?: string;
    user_display_name?: string;
  };
}

export interface AuditEntry {
  id: string;
  type: string;
  attributes: {
    entry_type: string;
    timestamp: string;
    user_id: string | null;
    user_email: string;
    account_id: string | null;
    account_name: string | null;
    details: Record<string, any>;
  };
}

interface ListResponse<T> {
  data: T[];
  links?: { self: string };
  meta?: { count: number };
}

interface SingleResponse<T> {
  data: T;
}

// --- Accounts ---

export interface InternalAccountsParams {
  page?: number;
  size?: number;
  sort?: Array<{ field: string; sort?: 'asc' | 'desc' }>;
  showDeactivated?: boolean;
}

export const useInternalAccounts = (params: InternalAccountsParams = {}) => {
  const page = params.page || 1;
  const size = params.size || 25;
  let sortStr = 'name';
  if (params.sort?.length) {
    const s = params.sort[0];
    sortStr = (s.sort === 'desc' ? '-' : '') + s.field;
  }

  return useQuery<ListResponse<InternalAccount>>({
    queryKey: ['internalAccounts', page, size, sortStr, params.showDeactivated],
    queryFn: async () => {
      const response = await axios.get(`${BASE_URL}/internal/accounts`, {
        withCredentials: true,
        params: {
          'page[number]': page,
          'page[size]': size,
          sort: sortStr,
          ...(params.showDeactivated && {
            'filter[show_deactivated]': 'true',
          }),
        },
      });
      return response.data;
    },
  });
};

export const useCreateAccount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { name: string; title?: string }) => {
      const response = await axios.post(
        `${BASE_URL}/internal/accounts`,
        params,
        { withCredentials: true },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['internalAccounts'] });
    },
  });
};

export const useInternalAccount = (accountId: string | undefined) => {
  return useQuery<SingleResponse<InternalAccount>>({
    queryKey: ['internalAccount', accountId],
    queryFn: async () => {
      const response = await axios.get(
        `${BASE_URL}/internal/accounts/${accountId}`,
        { withCredentials: true },
      );
      return response.data;
    },
    enabled: Boolean(accountId),
  });
};

export const useDeactivateAccount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      accountId: string;
      action: 'deactivate' | 'reactivate';
    }) => {
      const response = await axios.patch(
        `${BASE_URL}/internal/accounts/${params.accountId}`,
        { action: params.action },
        { withCredentials: true },
      );
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['internalAccounts'] });
      queryClient.invalidateQueries({
        queryKey: ['internalAccount', variables.accountId],
      });
      queryClient.invalidateQueries({ queryKey: ['assumedRoles'] });
      queryClient.invalidateQueries({ queryKey: ['activeAssumedRoles'] });
      queryClient.invalidateQueries({ queryKey: ['users', 'me'] });
    },
  });
};

// --- Account Users ---

export const useInternalAccountUsers = (accountId: string | undefined) => {
  return useQuery<ListResponse<InternalAccountUser>>({
    queryKey: ['internalAccountUsers', accountId],
    queryFn: async () => {
      const response = await axios.get(
        `${BASE_URL}/internal/accounts/${accountId}/users`,
        { withCredentials: true },
      );
      return response.data;
    },
    enabled: Boolean(accountId),
  });
};

export const useAddInternalAccountUser = (accountId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { email: string; role: string }) => {
      const response = await axios.post(
        `${BASE_URL}/internal/accounts/${accountId}/users`,
        params,
        { withCredentials: true },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['internalAccountUsers', accountId],
      });
    },
  });
};

export const useUpdateInternalAccountUser = (accountId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { userId: string; role: string }) => {
      const response = await axios.patch(
        `${BASE_URL}/internal/accounts/${accountId}/users/${params.userId}`,
        { role: params.role },
        { withCredentials: true },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['internalAccountUsers', accountId],
      });
    },
  });
};

export const useRemoveInternalAccountUser = (accountId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      await axios.delete(
        `${BASE_URL}/internal/accounts/${accountId}/users/${userId}`,
        { withCredentials: true },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['internalAccountUsers', accountId],
      });
    },
  });
};

// --- Assumed Roles ---

export const useAssumedRoles = () => {
  return useQuery<ListResponse<AssumedRole>>({
    queryKey: ['assumedRoles'],
    queryFn: async () => {
      const response = await axios.get(`${BASE_URL}/internal/assumed-roles`, {
        withCredentials: true,
      });
      return response.data;
    },
  });
};

export const useAccountAssumedRoles = (accountId: string | undefined) => {
  return useQuery<ListResponse<AssumedRole>>({
    queryKey: ['assumedRoles', 'account', accountId],
    queryFn: async () => {
      const response = await axios.get(
        `${BASE_URL}/internal/assumed-roles?filter[account_id]=${accountId}&filter[user_id]=all`,
        { withCredentials: true },
      );
      return response.data;
    },
    enabled: !!accountId,
  });
};

export const useActiveAssumedRoles = () => {
  return useQuery<ListResponse<AssumedRole>>({
    queryKey: ['activeAssumedRoles'],
    queryFn: async () => {
      const response = await axios.get(
        `${BASE_URL}/internal/assumed-roles/active`,
        { withCredentials: true },
      );
      return response.data;
    },
  });
};

export const useAssumeRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      account_id: string;
      assumed_role: string;
      reason: string;
      duration_hours: number;
    }) => {
      const response = await axios.post(
        `${BASE_URL}/internal/assumed-roles`,
        params,
        { withCredentials: true },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assumedRoles'] });
      queryClient.invalidateQueries({ queryKey: ['activeAssumedRoles'] });
      queryClient.invalidateQueries({ queryKey: ['users', 'me'] });
    },
  });
};

export const useEndAssumedRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (assumedRoleId: string) => {
      await axios.delete(
        `${BASE_URL}/internal/assumed-roles/${assumedRoleId}`,
        { withCredentials: true },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assumedRoles'] });
      queryClient.invalidateQueries({ queryKey: ['activeAssumedRoles'] });
      queryClient.invalidateQueries({ queryKey: ['users', 'me'] });
    },
  });
};

// --- Catalog Types (for entitlement dialogs) ---

export interface CatalogTypeItem {
  name: string;
  title: string;
  entity_type: string;
  release_status: string;
}

export const useInternalCatalogTypes = (entityType: string) => {
  return useQuery<CatalogTypeItem[]>({
    queryKey: ['internalCatalogTypes', entityType],
    queryFn: async () => {
      const response = await axios.get(`${BASE_URL}/catalog-types`, {
        withCredentials: true,
        params: {
          'filter[entity_type]': entityType,
          'page[size]': 1000,
        },
      });
      return (response.data.data || []).map((item: any) => ({
        name: item.attributes?.name || item.name,
        title: item.attributes?.title || item.title,
        entity_type: item.attributes?.entity_type || item.entity_type,
        release_status: item.attributes?.release_status || item.release_status,
      }));
    },
    staleTime: 300000,
  });
};

// --- Entitlements ---

export interface AccountEntitlement {
  id: string;
  type: 'entitlement';
  attributes: {
    entitlement_type: 'feature' | 'limit';
    entitlement_key: string;
    value: string | null;
    created_at: string;
    updated_at: string;
  };
}

export const useInternalAccountEntitlements = (
  accountId: string | undefined,
) => {
  return useQuery<ListResponse<AccountEntitlement>>({
    queryKey: ['internalEntitlements', accountId],
    queryFn: async () => {
      const response = await axios.get(
        `${BASE_URL}/internal/accounts/${accountId}/entitlements`,
        { withCredentials: true },
      );
      return response.data;
    },
    enabled: Boolean(accountId),
  });
};

export const useAddEntitlement = (accountId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      entitlement_type: string;
      entitlement_key: string;
      value?: string | null;
    }) => {
      const response = await axios.post(
        `${BASE_URL}/internal/accounts/${accountId}/entitlements`,
        params,
        { withCredentials: true },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['internalEntitlements', accountId],
      });
    },
  });
};

export const useRemoveEntitlement = (accountId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (entitlementId: string) => {
      await axios.delete(
        `${BASE_URL}/internal/accounts/${accountId}/entitlements/${entitlementId}`,
        { withCredentials: true },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['internalEntitlements', accountId],
      });
    },
  });
};

export const useUpdateEntitlement = (accountId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { entitlementId: string; value: string }) => {
      const response = await axios.patch(
        `${BASE_URL}/internal/accounts/${accountId}/entitlements/${params.entitlementId}`,
        { value: params.value },
        { withCredentials: true },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['internalEntitlements', accountId],
      });
    },
  });
};

// --- Account Domains ---

export interface AccountDomain {
  id: string;
  type: 'account-domain';
  attributes: {
    account_id: string;
    domain: string;
    created_at: string;
    updated_at: string;
  };
}

export const useInternalAccountDomains = (accountId: string | undefined) => {
  return useQuery<ListResponse<AccountDomain>>({
    queryKey: ['internalDomains', accountId],
    queryFn: async () => {
      const response = await axios.get(
        `${BASE_URL}/internal/accounts/${accountId}/domains`,
        { withCredentials: true },
      );
      return response.data;
    },
    enabled: Boolean(accountId),
  });
};

export const useAddDomain = (accountId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (domain: string) => {
      const response = await axios.post(
        `${BASE_URL}/internal/accounts/${accountId}/domains`,
        { data: { attributes: { domain } } },
        { withCredentials: true },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['internalDomains', accountId],
      });
    },
  });
};

export const useRemoveDomain = (accountId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (domainId: string) => {
      await axios.delete(
        `${BASE_URL}/internal/accounts/${accountId}/domains/${domainId}`,
        { withCredentials: true },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['internalDomains', accountId],
      });
    },
  });
};

// --- Audit Log ---

export const useAuditLog = () => {
  return useQuery<ListResponse<AuditEntry>>({
    queryKey: ['auditLog'],
    queryFn: async () => {
      const response = await axios.get(`${BASE_URL}/internal/audit-log`, {
        withCredentials: true,
      });
      return response.data;
    },
  });
};
